/* eslint-disable */

import React, { useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { useTheme } from '@mui/material/styles';
import am4themes_animated from '@amcharts/amcharts4/themes/animated'; /* eslint-disable-line camelcase */

// custom hook for creating XY amCharts in Simplifi fashion
export default function useXYChart(divId, useDate, showScrollBar, noOfMonths, withLegend) {

  const chart = useRef(null);
  const theme = useTheme();

  const [xAxis, setXAxis] = useState(null);
  const [yAxis, setYAxis] = useState(null);
  // const [loadingContainer, setLoadingContainer] = useState(null);  // disabled until we get the eventDispatcher bug under control

  // builds html string for a series to be added to a tooltip, uses series.dataFields.categoryX, series.dataFields.valueY, and series.dataFields.custom for color
  const buildTooltipSeriesInfoForSimpleBarChart = useCallback((series, compact=false, percentage=false, customStyles='') =>
    `<div style="${customStyles} width: ${compact ? 130 : 172}px;display: flex;flex-direction: row;align-items: center;color: ${theme.components.tooltip.textColor}" >
      <div style=";background: {${series.dataFields.custom}};min-width: 12px;min-height: 12px;border-radius: 50%;margin-right: 3px;" ></div>
      <span style="flex-grow: 1;color: inherit;display: flex;justify-content: space-between;font-size: 14px;min-width: ${compact ? 0 : 140}px" >
        <span style="flex: 2;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;text-transform: capitalize;" id="tooltip_category" >{${series.dataFields.categoryX}}</span>
        <span style="flex: 1;text-align: right;" id="tooltip_amount" >${percentage ? '' : '$'}{${series.dataFields.valueY}}${percentage ? '%' : ''}</span>
      </span>
    </div>`, [theme]);

  // builds html string for a series to be added to a tooltip, uses series.name, series.dataFields.valueY, and series.fill
  const buildTooltipSeriesInfo = useCallback((series, compact=false, percentage=false, customStyles='', withLegend = true) =>
    `<div style="${customStyles} width: ${compact ? 130 : 172}px;display: flex;flex-direction: row;align-items: center;color: ${theme.components.tooltip.textColor}" >
      ${withLegend ? `<div style=";background: ${series.fill};min-width: 12px;min-height: 12px;margin-right: 3px;" ></div>` : ''}
      <span style="flex-grow: 1;color: inherit;display: flex;justify-content: space-between;font-size: 14px;min-width: ${compact ? 0 : 140}px" >
        <span style="flex: 2;white-space: nowrap;overflow: hidden;text-overflow: ellipsis;text-transform: capitalize;" id="tooltip_category" >${series.name}</span>
        <span style="flex: 1;text-align: right;" id="tooltip_amount" >${series.name !== 'total' ? `${percentage ? '' : '$'}{${series.dataFields.valueY}}${percentage ? '%' : ''}` : '{totalValues.sign}\${totalValues.value}'}</span>
      </span>
    </div>`, [theme]);

  // builds a tooltip on a given amCharts object
  const buildTooltipOn = useCallback((paramObj, sideOfPointer = false, hideDefaultShadow = false) => {
    const obj = paramObj;

    if (!sideOfPointer) {
      obj.tooltip = new am4core.Tooltip();
    }

    obj.tooltip.getFillFromObject = false;
    obj.tooltip.background.fillOpacity = 0.9;
    obj.tooltip.background.fill = am4core.color(theme.components.tooltip.backgroundColor)
    obj.tooltip.label.padding(0, 0, 0, 0);
    obj.tooltip.background.strokeWidth = 0;
    obj.tooltip.background.cornerRadius = theme.shape.borderRadius * 2;
    obj.tooltip.background.valign = 'middle';
    obj.tooltip.zIndex = Infinity;
    obj.tooltip.interactionsEnabled = false;
    obj.tooltip.showInViewport = true;
    if (!sideOfPointer) {
      obj.tooltip.background.pointerLength = 0;
      obj.tooltip.pointerOrientation = 'vertical';
    }

    if (!hideDefaultShadow) {
      const shadow = obj.tooltip.background.filters.getIndex(0);
      shadow.dx = 0;
      shadow.dy = 2;
      shadow.blur = 4;
      shadow.spread = 0;
      shadow.opacity = 0.8;
      shadow.color = am4core.color(theme.applyOpacityToHex(theme.palette.grey.level3, 0.9));
    }

    return obj;
  }, [theme]);

  // creates a range between categories in the xAxis, returns the range
  const createCategoryRange = useCallback((startCategory, endCategory=null) => {
    const range = xAxis.axisRanges.create();
    if (useDate) {
      range.date = startCategory;
      range.endDate = endCategory || startCategory;
    } else {
      range.category = startCategory;
      range.endCategory = endCategory || startCategory;
    }
    range.label.disabled = true;
    range.axisFill.interactionsEnabled = true;
    range.axisFill.isMeasured = true;

    return range;
  }, [useDate, xAxis]);

  // builds a legend on the chart, disposes old legend
  const buildLegend = useCallback(() => {
    const tempChart = chart.current;
    if (!tempChart) return;

    tempChart.legend?.dispose?.();  // clean up old legend if it exists

    if (withLegend) {
      tempChart.legend = new am4charts.Legend();
      tempChart.legend.useDefaultMarker = true;
      tempChart.legend.labels.template.fill = am4core.color(theme.palette.text.primary);
      tempChart.legend.markers.template.width = 16;
      tempChart.legend.markers.template.height = 16;
      tempChart.legend.markers.template.children.getIndex(0).cornerRadius(2, 2, 2, 2);
      tempChart.legend.interactionsEnabled = false;
    }

    // return tempChart.legend;
  }, [chart, theme.palette.text.primary, withLegend]);

  // builds a default, active, and inactive state on a given amCharts object
  // also handles lightening of color and showing tooltip for the given states
  const makeSelectionStates = useCallback((paramObj, color, inactiveColor, setDefault = true) => {
    const defaultState = paramObj.states.getKey("default");
    if (defaultState && setDefault) { // in case the object doesn't have a default state, bullets for some reason are funky
      if (color) {
        defaultState.properties.fill = color;
        defaultState.properties.stroke = color;
      }
      defaultState.properties.showTooltipOn = "hover";
    }

    const activeState = paramObj.states.create("active");
    if (color) {
      activeState.properties.fill = color;
      activeState.properties.stroke = color;
    }
    activeState.properties.showTooltipOn = "hover";

    const inactiveState = paramObj.states.create("inactive");
    if (inactiveColor) {
      inactiveState.properties.fill = inactiveColor;
      inactiveState.properties.stroke = inactiveColor;
    } else if (!inactiveColor && color){
      inactiveState.properties.fill = color.lighten(0.8);
      inactiveState.properties.stroke = color.lighten(0.8);
    }
    inactiveState.properties.showTooltipOn = "hover";

    const archivedState = paramObj.states.create("archived");
    archivedState.properties.fill = am4core.color(theme.palette.greyScaleDeprecated[5]);
    archivedState.properties.stroke = am4core.color(theme.palette.greyScaleDeprecated[5]);

    return { defaultState, activeState, inactiveState };  // return states so they can be further adjusted
  }, [theme]);

  // main effect building the chart
  useLayoutEffect(() => {
    am4core.useTheme(am4themes_animated);
    am4core.options.autoDispose = true;
    const tempChart = am4core.create(divId, am4charts.XYChart);

    // needed for tooltips
    tempChart.cursor = new am4charts.XYCursor();
    tempChart.cursor.lineX.disabled = true;
    tempChart.cursor.lineY.disabled = true;
    tempChart.cursor.behavior = "none";

    let categoryAxis;
    if (useDate) {
      categoryAxis = tempChart.xAxes.push(new am4charts.DateAxis());
      categoryAxis.dataFields.dateX = 'date';
    } else {
      categoryAxis = tempChart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'category';
    }
    categoryAxis.tooltip.disabled = true;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.grid.template.location = 0.5;
    categoryAxis.renderer.grid.template.strokeOpacity = 0;
    categoryAxis.renderer.cellStartLocation = 0.2;
    categoryAxis.renderer.cellEndLocation = 0.8;
    categoryAxis.renderer.ticks.template.disabled = false;
    categoryAxis.renderer.line.opacity = 1;
    if (!useDate) {
      categoryAxis.renderer.minGridDistance = 20;
    }
    categoryAxis.renderer.labels.template.fontSize = '0.75rem';
    categoryAxis.renderer.labels.template.fontWeight = 400;
    categoryAxis.renderer.labels.template.fill = theme.typography.caption.color;
    categoryAxis.renderer.labels.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;

    if (!tempChart.isDisposed()) { // eslint-disable-line
      // handles hovering the category labels to trigger a hover over the y axis
      categoryAxis.renderer.labels.template.events.on("over", (e) => {
        let point;
        if (useDate) {
          point = categoryAxis.dateToPoint(e.target.dataItem.date);
        } else {
          point = categoryAxis.categoryToPoint(e.target.dataItem.category);
        }
        tempChart.cursor.triggerMove(point, "soft");
      });
      categoryAxis.renderer.labels.template.events.on("out", (e) => {
        let point;
        if (useDate) {
          point = categoryAxis.dateToPoint(e.target.dataItem.date);
        } else {
          point = categoryAxis.categoryToPoint(e.target.dataItem.category);
        }
        tempChart.cursor.triggerMove(point, "none");
      });
    }


    const valueAxis = tempChart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.grid.template.disabled = true;
    valueAxis.tooltip.disabled = true;
    valueAxis.renderer.line.opacity = 0;
    valueAxis.renderer.line.zIndex = -1;
    valueAxis.renderer.ticks.template.disabled = true;
    valueAxis.renderer.labels.template.fontSize = '0.75rem';
    valueAxis.renderer.labels.template.fontWeight = 400;
    valueAxis.renderer.labels.template.fill = theme.typography.caption.color;
    valueAxis.numberFormatter = new am4core.NumberFormatter();
    valueAxis.numberFormatter.numberFormat = "'$'#,###.## | '-$'#,###.##s";
    valueAxis.renderer.baseGrid.stroke = am4core.color(theme.palette.greyScaleDeprecated[2]);
    valueAxis.renderer.baseGrid.zIndex = 1;
    valueAxis.renderer.minGridDistance = 25;
    valueAxis.extraMax = 0.1;
    valueAxis.calculateTotals = true;

    tempChart.maxZoomLevel = 1;
    tempChart.zoomOutButton.disabled = true;

    // TODO: loading indicator for graph
    // tempChart.preloader.disabled = true;
    // const loadingIndicator = tempChart.tooltipContainer.createChild(am4core.Container);
    // loadingIndicator.background.fill = am4core.color(theme.palette.greyScaleDeprecated[7]);
    // loadingIndicator.background.fillOpacity = 1;
    // loadingIndicator.width = am4core.percent(100);
    // loadingIndicator.height = am4core.percent(100);
    //
    // const box = loadingIndicator.createChild(am4core.Label);
    // box.text = "Loading stuff...";
    // box.align = "center";
    // box.valign = "middle";
    // box.fontSize = 20;
    // box.dy = 50;
    //
    // tempChart.events.on('beforedatavalidated', () => {
    //   loadingIndicator.show();
    // });
    // tempChart.events.on('dataitemsvalidated', () => {
    //   loadingIndicator.hide(1000);
    // });

    chart.current = tempChart;

    // add horizontal scroll bar for over time chart if the period is more than 2 years
    if (showScrollBar && noOfMonths > 24) {
      tempChart.scrollbarX = new am4core.Scrollbar();
      tempChart.scrollbarX.parent = tempChart.bottomAxesContainer;

      // add zoom level dynamically
      categoryAxis.keepSelection = true;
      categoryAxis.start = noOfMonths > 36 ?  0.7 : 0.3;

      tempChart.cursor.behavior = 'panX';
      tempChart.zoomOutButton.disabled = true;
      tempChart.swipeable = true;

      tempChart.scrollbarX.startGrip.background.fill = am4core.color(theme.components.report.chartScrollGripIconColor);
      tempChart.scrollbarX.endGrip.background.fill = am4core.color(theme.components.report.chartScrollGripIconColor);
      tempChart.scrollbarX.startGrip.icon.disabled = true;
      tempChart.scrollbarX.endGrip.icon.disabled = true;
      tempChart.scrollbarX.thumb.background.fill = am4core.color(theme.components.report.chartScrollGripBackground);
      tempChart.scrollbarX.thumb.background.states.getKey("down").properties.fill = am4core.color(theme.components.report.chartScrollGripBackground);
      tempChart.scrollbarX.thumb.background.states.getKey("hover").properties.fill = am4core.color(theme.components.report.chartScrollGripBackground);

      // add the scroll bar from zero index
      tempChart.events.on("ready", function () {
        categoryAxis.zoomToIndexes(0, noOfMonths > 36 ? 20 : 10, false, true);
      });
    }
    setXAxis(categoryAxis);
    setYAxis(valueAxis);

    return () => { tempChart && tempChart?.dispose?.(); };
  }, [divId, theme, useDate, showScrollBar, noOfMonths]);

  return {
    chart: chart.current,
    xAxis,
    yAxis,
    buildTooltipOn,
    buildTooltipSeriesInfo,
    buildTooltipSeriesInfoForSimpleBarChart,
    buildLegend,
    createCategoryRange,
    makeSelectionStates,
  }
}



// ================================================================================================================== //
//                                                   Usage/Example                                                    //
// ================================================================================================================== //

const exampleData = [
  { category: 'Jan', simplifi: 100, quicken: 150 },
  { category: 'Feb', simplifi: 150, quicken: 170 },
  { category: 'Mar', simplifi: 250, quicken: 200 },
];

export function ExampleCharts(_props) {
  const { chart: barChart, buildLegend: buildBarChartLegend } = useXYChart('example-bar-chart-div');
  const { chart: multilineChart, buildTooltipOn: buildMultilineChartTooltipOn,
    buildTooltipSeriesInfo: buildMultilineChartTooltipSeriesInfo } = useXYChart('example-multiline-chart-div');

  const createColumnSeries = useCallback((name, field) => {
    const series = barChart.series.push(new am4charts.ColumnSeries());
    series.name = name;
    series.dataFields.valueY = field;
    series.dataFields.categoryX = 'category'; // needs to be category to work with the axis
  }, [barChart]);

  const createLineSeries = useCallback((name, field) => {
    const series = multilineChart.series.push(new am4charts.LineSeries());
    series.name = name;
    series.dataFields.valueY = field;
    series.dataFields.categoryX = 'category'; // needs to be category to work with the axis

    buildMultilineChartTooltipOn(series);
    series.tooltipHTML = buildMultilineChartTooltipSeriesInfo(series);
  }, [multilineChart, buildMultilineChartTooltipOn, buildMultilineChartTooltipSeriesInfo]);

  useLayoutEffect(() => {
    if (!barChart || !multilineChart) return;

    createColumnSeries('simplifi', 'simplifi');
    createColumnSeries('quicken', 'quicken');
    barChart.data = exampleData;  // if the data is going to be changing it should get set in a separate useLayoutEffect
    buildBarChartLegend();

    createLineSeries('simplifi', 'simplifi');
    createLineSeries('quicken', 'quicken');
    multilineChart.data = exampleData  // if the data is going to be changing it should get set in a separate useLayoutEffect

  }, [barChart, multilineChart, buildBarChartLegend, createColumnSeries, createLineSeries]);

  const chartStyle = useMemo(() => ({ width: 500, height: 200 }), [])

  return (
    <div style={{ display: 'flex' }} >
      <div>
        <p>Bar Chart, using legend helpers</p>
        <div style={chartStyle} id="example-bar-chart-div" />
      </div>
      <div>
        <p>Multiline Chart, using tooltip helpers</p>
        <div style={chartStyle} id="example-multiline-chart-div" />
      </div>
    </div>
  )
}
